import React, { useContext, useState, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from "react";
import { isSameDay, parseISO, format, subDays, differenceInCalendarDays } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import clsx from "clsx";
import { isNil } from "lodash";
import {
  Button,
  Divider,
  Typography,
  IconButton,
  makeStyles
} from "@material-ui/core";

import {
  AccessTime,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  InsertEmoticon,
  KeyboardArrowDown,
  Close,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  WhatsApp
} from "@material-ui/icons";
import LockIcon from '@material-ui/icons/Lock';
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import { getTokens, brandAlpha, SEMANTIC } from "../../styles/tokens";
import YouTubePreview from "../ModalYoutubeCors";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import AdMetaPreview from "../AdMetaPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import FileCard, { fileInfoFromUrl } from "./FileCard";
import VideoBubble from "./VideoBubble";
import UnreadDivider, { findFirstUnreadId } from "./UnreadDivider";
import useReactions from "./useReactions";
import { isReactionRow } from "./reactions";
import ReactionChips from "./ReactionChips";
import ReactionPicker from "./ReactionPicker";
import PendingBubble, { SendErrorRow } from "./PendingBubble";
import {
  usePendingMessages,
  matchRealMessage,
  reconcilePending,
  removePending,
  onPendingReal,
  onPendingStale,
  getPreviewFor,
} from "./pendingStore";
import { resendMessage } from "./resend";
import LinkPreviewCard from "./LinkPreviewCard";
import { linkPreviewOf } from "./linkPreview";
import { onJumpToMessage, requestJumpToMessage, scrollToMessage } from "./jumpToMessage";
import { toast } from "react-toastify";
import MediaGallery from "./MediaGallery";
import MentionText from "./MentionText";
import useGroupMentions from "./useGroupMentions";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { CircularProgress } from "@material-ui/core";
import { useParams, useHistory } from 'react-router-dom';
import { downloadResource } from "../../utils";
import Template from "./templates";
import { usePdfViewer } from "../../hooks/usePdfViewer";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const isDark = theme.palette.type === "dark";
  // Conversa no padrão do WhatsApp Web: paleta fixa (não segue a cor da marca), para o
  // atendente reconhecer de imediato o que é recebido, enviado, lido e nota interna.
  const wa = isDark
    ? {
        bg: "#0b141a",
        incoming: "#202c33",
        outgoing: "#005c4b",
        note: "#4a3f14",
        text: "#e9edef",
        meta: "rgba(233,237,239,0.6)",
        tick: "rgba(233,237,239,0.5)",
        read: "#53bdeb",
        pill: "#182229",
        pillText: "#8696a0",
        quote: "rgba(11,20,26,0.35)",
        doodle: "%23ffffff",
        doodleOpacity: "0.03",
      }
    : {
        bg: "#efeae2",
        incoming: "#ffffff",
        outgoing: "#d9fdd3",
        note: "#fff3c4",
        text: "#111b21",
        meta: "#667781",
        tick: "#8696a0",
        read: "#53bdeb",
        pill: "#ffffff",
        pillText: "#54656f",
        quote: "rgba(11,20,26,0.06)",
        doodle: "%23000000",
        doodleOpacity: "0.032",
      };
  const waShadow = "0 1px 0.5px rgba(11,20,26,0.13)";
  // Papel de parede: desenhos de traço fino, bem apagados, em mosaico.
  const waDoodle = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180' fill='none' stroke='${wa.doodle}' stroke-opacity='${wa.doodleOpacity}' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 26h22a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6H30l-8 7v-7h-4a6 6 0 0 1-6-6V32a6 6 0 0 1 6-6z'/%3E%3Ccircle cx='112' cy='30' r='11'/%3E%3Cpath d='M107 30h10M112 25v10'/%3E%3Cpath d='M150 70l6 12 13 2-9 9 2 13-12-6-12 6 2-13-9-9 13-2z'/%3E%3Cpath d='M22 104c8-10 20-10 28 0s20 10 28 0'/%3E%3Crect x='96' y='98' width='26' height='20' rx='4'/%3E%3Cpath d='M96 103l13 9 13-9'/%3E%3Cpath d='M34 146a10 10 0 1 0 14 14'/%3E%3Cpath d='M30 142l5 5M56 166l-5-5'/%3E%3Cpath d='M92 150h30M92 158h20M92 166h26'/%3E%3Cpath d='M150 140v24M138 152h24'/%3E%3Cpath d='M70 62c0-8 12-8 12 0 0 6-6 8-6 14M76 84v.5'/%3E%3C/svg%3E")`;
  const bubbleBase = {
    marginTop: 2,
    minWidth: 82,
    maxWidth: "min(640px, 65%)",
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    fontSize: 14.2,
    lineHeight: "19px",
    color: wa.text,
    paddingLeft: 9,
    paddingRight: 7,
    paddingTop: 6,
    paddingBottom: 0,
    borderRadius: 7.5,
    boxShadow: waShadow,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "86%",
    },
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 2,
      right: 2,
    },
    "&:hover $reactBtn, &:focus-within $reactBtn": {
      opacity: 1,
      pointerEvents: "auto",
    },
  };
  return {
    messagesListWrapper: {
      overflow: "hidden",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      width: "100%",
      minWidth: 300,
      minHeight: 200,
      backgroundColor: wa.bg,
    },
    daySection: {
      display: "flex",
      flexDirection: "column",
      flex: "none",
    },
    // área rolável + botão de descer (o aviso de 24h da Meta fica abaixo, fora dela)
    listArea: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      minHeight: 0,
    },
    scrollDownBtn: {
      position: "absolute",
      right: 16,
      bottom: 16,
      zIndex: 5,
      width: 42,
      height: 42,
      padding: 0,
      borderRadius: "50%",
      backgroundColor: wa.incoming,
      color: wa.meta,
      boxShadow: "0 1px 1px rgba(11,20,26,0.06), 0 2px 5px rgba(11,20,26,0.2)",
      "&:hover": { backgroundColor: wa.incoming },
    },
    scrollDownBadge: {
      position: "absolute",
      top: -7,
      right: -3,
      minWidth: 20,
      height: 20,
      padding: "0 5px",
      borderRadius: 10,
      backgroundColor: "#25d366",
      color: "#ffffff",
      fontSize: 11.5,
      fontWeight: 700,
      lineHeight: "20px",
      textAlign: "center",
    },
    // separador de ticket (pill neutra + ponto na cor da fila)
    currentTick: {
      alignSelf: "center",
      display: "flex",
      justifyContent: "center",
      width: "100%",
      margin: "16px 0 8px",
    },
    currentTicktText: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 12px",
      borderRadius: 7.5,
      backgroundColor: wa.pill,
      boxShadow: waShadow,
      fontSize: 12,
      fontWeight: 500,
      color: wa.pillText,
    },
    currentTickDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },

    messagesList: {
      backgroundColor: wa.bg,
      backgroundImage: waDoodle,
      backgroundSize: "180px 180px",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      padding: "10px clamp(18px, 6%, 84px) 16px",
      overflowY: "auto",
      [theme.breakpoints.down("sm")]: {
        padding: "8px 14px 14px",
      },
      ...theme.scrollbarStyles,
    },
    dragElement: {
      background: isDark ? "rgba(11,18,32,0.88)" : "rgba(255,255,255,0.88)",
      position: "absolute",
      width: "100%",
      height: "100%",
      zIndex: 999999,
      textAlign: "center",
      fontSize: "1.5em",
      fontWeight: 600,
      border: `2px dashed ${t.brand}`,
      color: t.brand,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    circleLoading: {
      color: t.brand,
      position: "absolute",
      opacity: "70%",
      top: 0,
      left: "50%",
      marginTop: 12,
    },

    // balão recebido: papel + borda line, canto inferior esquerdo 4
    messageLeft: {
      ...bubbleBase,
      marginRight: 28,
      alignSelf: "flex-start",
      backgroundColor: wa.incoming,
    },
    // modo de seleção (encaminhar): abre espaço à esquerda para a caixa de seleção
    selectingLeft: {
      marginLeft: 36,
    },

    quotedContainerLeft: {
      margin: "0 0 8px",
      overflow: "hidden",
      backgroundColor: wa.quote,
      borderRadius: 7.5,
      display: "flex",
      position: "relative",
    },

    quotedMsg: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      display: "block",
      whiteSpace: "pre-wrap",
      overflow: "hidden",
    },

    quotedSideColorLeft: {
      flex: "none",
      width: 4,
      backgroundColor: t.accent,
    },

    // balão enviado: tinta média da marca, texto ink, canto inferior direito 4
    messageRight: {
      ...bubbleBase,
      marginLeft: 28,
      alignSelf: "flex-end",
      backgroundColor: wa.outgoing,
    },

    // mensagem privada: tinta de aviso + cadeado
    messageRightPrivate: {
      ...bubbleBase,
      marginLeft: 28,
      alignSelf: "flex-end",
      backgroundColor: wa.note,
    },

    quotedContainerRight: {
      margin: "0 0 8px",
      overflowY: "hidden",
      backgroundColor: wa.quote,
      borderRadius: 7.5,
      display: "flex",
      position: "relative",
    },

    quotedMsgRight: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      whiteSpace: "pre-wrap",
    },

    quotedSideColorRight: {
      flex: "none",
      width: 4,
      backgroundColor: t.accent,
    },

    messageActionsButton: {
      display: "none",
      position: "relative",
      color: wa.meta,
      zIndex: 1,
      backgroundColor: "inherit",
      opacity: "90%",
      width: 28,
      height: 28,
      padding: 4,
      borderRadius: 9,
      "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
    },

    messageContactName: {
      display: "flex",
      color: isDark ? t.accent : t.accentHover,
      fontWeight: 600,
      fontSize: 12,
    },

    textContentItem: {
      overflowWrap: "break-word",
      padding: "0 62px 8px 0",
      // bolha sem texto próprio (template, mídia): reserva a linha da hora para ela não
      // ficar por cima do conteúdo
      minHeight: 19,
    },

    textContentItemDeleted: {
      fontStyle: "italic",
      color: wa.meta,
      overflowWrap: "break-word",
      padding: "0 62px 8px 0",
    },

    messageMedia: {
      width: "min(400px, 100%)",
      maxWidth: "100%",
      height: "auto",
      borderRadius: 12,
      "&[controls]": {
        objectFit: "contain",
      },
    },
    mediaSurface: {
      backgroundColor: t.muted,
      borderRadius: 12,
    },
    mediaError: {
      padding: 20,
      textAlign: "center",
      backgroundColor: t.muted,
      borderRadius: 12,
      color: t.textSecondary,
      marginTop: 8,
    },
    mediaErrorText: {
      padding: 10,
      textAlign: "center",
      fontSize: 12,
      color: t.textMuted,
    },

    timestamp: {
      fontSize: 11,
      lineHeight: "15px",
      position: "absolute",
      bottom: 3,
      right: 7,
      color: wa.meta,
      display: "inline-flex",
      alignItems: "center",
      gap: 2,
    },
    privateIcon: {
      fontSize: 12,
      marginRight: 2,
      color: SEMANTIC.warning,
    },

    forwardMessage: {
      fontSize: 12,
      fontStyle: "italic",
      position: "absolute",
      top: 0,
      left: 5,
      color: t.textMuted,
      display: "flex",
      alignItems: "center",
    },
    forwardIcon: {
      color: t.textMuted,
      transform: "scaleX(-1)",
      fontSize: 16,
    },

    // separador de data: pill neutra 12px
    dailyTimestamp: {
      alignSelf: "center",
      // data flutuante: gruda no topo enquanto as mensagens daquele dia rolam
      position: "sticky",
      top: 4,
      zIndex: 3,
      margin: "12px 0 6px",
      padding: "5px 12px",
      borderRadius: 7.5,
      backgroundColor: wa.pill,
      boxShadow: waShadow,
    },

    dailyTimestampText: {
      color: wa.pillText,
      fontSize: 12,
      fontWeight: 500,
      textTransform: "uppercase",
      padding: 0,
      alignSelf: "center",
      marginLeft: 0,
    },

    ackIcons: {
      fontSize: 16,
      verticalAlign: "middle",
      marginLeft: 2,
      color: wa.tick,
    },

    ackError: {
      fontSize: 15,
      verticalAlign: "middle",
      marginLeft: 2,
      color: isDark ? "#ff8a80" : "#d93025",
    },

    deletedIcon: {
      fontSize: 16,
      verticalAlign: "middle",
      marginRight: 4,
    },

    ackDoneAllIcon: {
      color: wa.read,
      fontSize: 16,
      verticalAlign: "middle",
      marginLeft: 2,
    },

    ackPlayedIcon: {
      color: wa.read,
      fontSize: 16,
      verticalAlign: "middle",
      marginLeft: 2,
    },
    downloadMedia: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "inherit",
      padding: 10,
      color: t.textSecondary,
    },

    // chamada perdida / eventos centrais
    messageCenter: {
      marginTop: 5,
      alignSelf: "center",
      position: "relative",
      backgroundColor: wa.pill,
      boxShadow: waShadow,
      fontSize: 12,
      minWidth: 100,
      maxWidth: 270,
      color: wa.pillText,
      borderRadius: 7.5,
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 5,
      paddingBottom: 4,
      "&:hover #messageActionsButton": {
        display: "flex",
        position: "absolute",
        top: 0,
        right: 0,
      },
    },

    deletedMessage: {
      color: SEMANTIC.error,
    },

    // aviso de janela de 24h (Meta)
    channelNotice: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      backgroundColor: t.accentTint.soft,
      borderTop: `1px solid ${t.line}`,
      color: t.text,
      fontSize: 12.5,
      "& svg": { color: t.accentHover, fontSize: 20, flexShrink: 0 },
    },
    // aviso flutuante enquanto procura uma mensagem antiga (busca / clique na citação)
    jumpNotice: {
      position: "absolute",
      top: 10,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 6,
      display: "flex",
      alignItems: "center",
      gap: 10,
      maxWidth: "calc(100% - 24px)",
      padding: "7px 8px 7px 14px",
      borderRadius: 18,
      backgroundColor: wa.incoming,
      color: wa.text,
      boxShadow: "0 1px 1px rgba(11,20,26,0.06), 0 2px 5px rgba(11,20,26,0.2)",
      fontSize: 13,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      "& .MuiCircularProgress-root": { color: "#00a884", flex: "none" },
    },
    jumpNoticeText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    jumpNoticeCount: {
      color: wa.meta,
    },
    jumpNoticeClose: {
      width: 24,
      height: 24,
      padding: 0,
      borderRadius: "50%",
      color: wa.meta,
      "& svg": { fontSize: 16 },
    },
    // botão de reagir: aparece ao lado da bolha ao passar o mouse (no toque, vale o toque longo)
    reactSlot: {
      position: "absolute",
      top: 0,
      bottom: 0,
      width: 38,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "@media (hover: none)": { display: "none" },
    },
    reactSlotAfter: { left: "100%" },
    reactSlotBefore: { right: "100%" },
    reactBtn: {
      width: 26,
      height: 26,
      padding: 0,
      borderRadius: "50%",
      opacity: 0,
      pointerEvents: "none",
      color: "#ffffff",
      backgroundColor: isDark ? "rgba(233,237,239,0.22)" : "rgba(11,20,26,0.28)",
      transition: "opacity 120ms ease",
      "&:hover": { backgroundColor: isDark ? "rgba(233,237,239,0.32)" : "rgba(11,20,26,0.4)" },
      "&.Mui-focusVisible": { opacity: 1, pointerEvents: "auto" },
      "& svg": { fontSize: 18 },
    },
    // bolha com selinhos de reação: espaço embaixo para eles não encostarem na próxima
    withReactions: {
      marginBottom: 20,
    },
    // primeira bolha de uma sequência: mais respiro e o "rabicho" no canto de cima
    tailLeft: {
      marginTop: 10,
      borderTopLeftRadius: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: -8,
        width: 8,
        height: 13,
        backgroundColor: "inherit",
        clipPath: "polygon(100% 0, 0 0, 100% 100%)",
      },
    },
    tailRight: {
      marginTop: 10,
      borderTopRightRadius: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        right: -8,
        width: 8,
        height: 13,
        backgroundColor: "inherit",
        clipPath: "polygon(0 0, 100% 0, 0 100%)",
      },
    },
    emptyMessages: {
      alignSelf: "center",
      margin: "auto",
      padding: "8px 14px",
      borderRadius: 7.5,
      backgroundColor: wa.pill,
      color: wa.pillText,
      fontSize: 12.5,
      fontWeight: 500,
      boxShadow: waShadow,
    },
  };
});

// Documento sem legenda guarda o próprio nome do arquivo no corpo: o cartão já mostra o nome,
// então o texto repetido embaixo é escondido.
const normalizeName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const bodyIsOnlyFileName = (message) => {
  if (!message.mediaUrl || !message.body) return false;
  if (["image", "video", "audio"].includes(message.mediaType)) return false;
  const body = normalizeName(message.body);
  return body.length > 0 && body === normalizeName(fileInfoFromUrl(message.mediaUrl).name);
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  // Reconexão do socket: mensagens que chegaram no intervalo entram no lugar certo (ordem por data).
  if (action.type === "MERGE_MESSAGES") {
    const merged = [...state];
    let changed = false;
    action.payload.forEach((message) => {
      const idx = merged.findIndex((m) => m.id === message.id);
      if (idx !== -1) {
        merged[idx] = message;
      } else {
        merged.push(message);
        changed = true;
      }
    });
    if (!changed) return merged;
    merged.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return merged;
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      // o evento pode vir sem as associações (contato, citação…): completa com o que já estava na tela
      state[messageIndex] = {
        ...state[messageIndex],
        ...messageToUpdate,
        sendError: messageToUpdate.sendError
      };
    }

    return [...state];
  }

  // Mensagem apagada do banco por outro usuário: sem este caso o redutor devolvia
  // undefined e a conversa inteira quebrava.
  if (action.type === "DELETE_MESSAGE") {
    return state.filter((m) => m.id !== action.payload);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  ticketStatus,
  unreadAtOpen,
  ticketNumericId,
  groupContactId
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();
  const listRef = useRef(null);
  const nearBottomRef = useRef(true); // o atendente está no fim da conversa?
  const prependAnchorRef = useRef(null); // distância até o fim antes de carregar mensagens antigas
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [newBelow, setNewBelow] = useState(0); // mensagens que chegaram enquanto lia as antigas

  // Faixa "N mensagens não lidas": decidida uma vez por abertura da conversa.
  const [unreadMarker, setUnreadMarker] = useState(null); // { beforeId, count }
  const [firstPageTicket, setFirstPageTicket] = useState(null); // conversa cuja 1ª página já chegou
  const unreadMarkerRef = useRef(null);
  const unreadDividerRef = useRef(null);
  const unreadDecidedRef = useRef(false);
  const unreadFromListRef = useRef(0);
  const unreadLeftBottomRef = useRef(false); // o atendente já esteve longe do fim com a faixa na tela?

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  // linhas de reação não viram bolha: viram os selinhos da mensagem-alvo
  const { visibleMessages, reactionsById, reactTo } = useReactions(messagesList, ticketId);

  // "Enviando": bolhas provisórias no fim da conversa, até a mensagem de verdade chegar.
  const pendingMessages = usePendingMessages(ticketNumericId);
  const ticketNumericIdRef = useRef(ticketNumericId);
  ticketNumericIdRef.current = ticketNumericId;
  const viewList = useMemo(() => {
    if (pendingMessages.length === 0) return visibleMessages;
    const last = visibleMessages[visibleMessages.length - 1];
    return [
      ...visibleMessages,
      ...pendingMessages.map((entry) => ({
        id: `pending-${entry.localId}`,
        pending: entry,
        fromMe: true,
        isPrivate: entry.isPrivate,
        body: entry.body,
        quotedMsg: entry.quotedMsg,
        createdAt: entry.createdAt,
        // mesma numeração de ticket da última mensagem: não abre separador de atendimento
        ticketId: last ? last.ticketId : undefined,
        ticket: last ? last.ticket : undefined,
      })),
    ];
  }, [visibleMessages, pendingMessages]);
  const [resendState, setResendState] = useState({}); // id da mensagem -> "busy"

  // "Não enviada": o servidor avisou pelo socket (sendError) ou, como esse aviso não fica
  // gravado, a mensagem é nossa e continua só com o relógio minutos depois.
  const UNSENT_AFTER_MS = 3 * 60 * 1000;
  const [, setUnsentTick] = useState(0);
  const isUnsent = (message) => {
    if (!message || message.pending || !message.fromMe || message.isPrivate || message.isDeleted) return false;
    if (message.sendError) return true;
    if (message.ack !== 0 || typeof message.id !== "number") return false;
    // só mensagem do envio pela tela (id gerado pelo Baileys). Mensagens gravadas por robô, régua e IA
    // também ficam em ack 0 e não são "não enviadas".
    if (!/^3EB0[0-9A-F]{36}$/.test(String(message.wid || ""))) return false;
    return Date.now() - new Date(message.createdAt).getTime() > UNSENT_AFTER_MS;
  };
  // enquanto houver mensagem recente só com o relógio, reavalia de tempos em tempos
  const hasFreshClock = visibleMessages.some(
    (m) => m.fromMe && m.ack === 0 && !m.isPrivate && !m.sendError && Date.now() - new Date(m.createdAt).getTime() <= UNSENT_AFTER_MS
  );
  useEffect(() => {
    if (!hasFreshClock) return undefined;
    const timer = setInterval(() => setUnsentTick((n) => n + 1), 20000);
    return () => clearInterval(timer);
  }, [hasFreshClock]);

  // Grupo: "@<id>" do texto vira "@Nome" do participante marcado.
  const { resolveMention } = useGroupMentions({
    enabled: !!isGroup,
    contactId: groupContactId,
    ticketId: ticketNumericId,
    messages: visibleMessages,
  });
  const renderBodyText = (text, message, plain = false) => (
    <MentionText text={text} plain={plain} resolve={isGroup ? (digits) => resolveMention(digits, message) : null} />
  );

  // Galeria: todas as fotos e vídeos já carregados da conversa, na ordem em que aparecem.
  const [galleryStartId, setGalleryStartId] = useState(null);
  const galleryItems = useMemo(
    () =>
      visibleMessages
        .filter((m) => m.mediaUrl && !m.isDeleted && (m.mediaType === "image" || m.mediaType === "video"))
        .map((m) => {
          const rawName = String(m.mediaUrl).split("?")[0].split("/").pop() || "";
          let fileName = rawName;
          try {
            fileName = decodeURIComponent(rawName);
          } catch (e) {
            /* nome com "%" solto: fica como veio */
          }
          return {
            id: m.id,
            type: m.mediaType,
            url: m.mediaUrl,
            createdAt: m.createdAt,
            author: m.fromMe ? i18n.t("conversation.reactions.you") : m.contact?.name || "",
            // mídia sem legenda guarda o nome do arquivo no corpo
            caption: m.body && ![fileName.trim(), rawName.trim()].includes(m.body.trim()) ? m.body : "",
          };
        }),
    [visibleMessages]
  );

  // "Ir até a mensagem" que ainda não está na tela: carrega páginas antigas até achar.
  const [jumpState, setJumpState] = useState(null); // { loaded }
  const jumpRef = useRef(null); // busca em andamento (para cancelar)
  const jumpScrollRef = useRef(null); // id para rolar assim que a mensagem estiver na tela
  const skipFetchPageRef = useRef(null); // página que a busca já trouxe (o efeito não repete)
  const pageNumberRef = useRef(1);
  const hasMoreRef = useRef(false);
  const [reactionTarget, setReactionTarget] = useState(null); // { anchorEl, message, startOnPicker }
  const longPressRef = useRef(null);

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);


  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  // Hook simplificado para PDF
  const {
    downloadPdf,
    extractPdfInfoFromMessage,
    isPdfUrl
  } = usePdfViewer();

  const { showSelectMessageCheckbox, selectedMessages, setSelectedMessages } = useContext(ForwardMessageContext);

  // Saiu do modo de seleção (encaminhou ou cancelou): esvazia a seleção, senão as mensagens
  // da vez anterior iam junto no próximo encaminhamento.
  useEffect(() => {
    if (!showSelectMessageCheckbox && selectedMessages.length > 0) {
      setSelectedMessages([]);
    }
  }, [showSelectMessageCheckbox]); // eslint-disable-line react-hooks/exhaustive-deps
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  pageNumberRef.current = pageNumber;
  hasMoreRef.current = hasMore;

  useEffect(() => {
    async function fetchData() {
      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {
        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
    nearBottomRef.current = true;
    prependAnchorRef.current = null;
    setShowScrollDown(false);
    setNewBelow(0);
    setFirstPageTicket(null);
    setGalleryStartId(null);
    cancelJump();
    skipFetchPageRef.current = null;
  }, [ticketId, selectedQueuesMessage]);

  const clearUnreadMarker = () => {
    if (!unreadMarkerRef.current) return;
    unreadMarkerRef.current = null;
    setUnreadMarker(null);
  };

  // Trocar o filtro de filas recarrega a lista, mas não é uma nova abertura da conversa.
  useEffect(() => {
    unreadDecidedRef.current = false;
    unreadFromListRef.current = 0;
    unreadLeftBottomRef.current = false;
    unreadMarkerRef.current = null;
    setUnreadMarker(null);
  }, [ticketId]);

  // O total de não lidas vem de duas respostas que correm em paralelo (a do ticket e a da
  // 1ª página de mensagens) e o servidor zera o contador logo depois de responder: vale a maior.
  useEffect(() => {
    if (unreadDecidedRef.current) return;
    if (firstPageTicket !== ticketId || unreadAtOpen === null) return;
    unreadDecidedRef.current = true;
    const count = Math.max(Number(unreadAtOpen) || 0, unreadFromListRef.current || 0);
    const beforeId = findFirstUnreadId(messagesList, count);
    if (!beforeId) return;
    unreadMarkerRef.current = { beforeId, count };
    setUnreadMarker(unreadMarkerRef.current);
  }, [firstPageTicket, unreadAtOpen, ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A conversa abre parada na faixa (com um pouco de contexto acima), não no fim.
  const scrollToUnreadDivider = () => {
    const el = listRef.current;
    const divider = unreadDividerRef.current;
    if (!el || !divider) return false;
    const top = divider.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
    el.scrollTop = Math.max(0, top - Math.round(el.clientHeight * 0.28));
    // se a posição não mudou, o navegador não dispara "scroll": acerta o estado aqui
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    nearBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
    if (!nearBottom) unreadLeftBottomRef.current = true;
    return true;
  };

  useLayoutEffect(() => {
    if (unreadMarker) scrollToUnreadDivider();
  }, [unreadMarker]);

  // Enviou: a bolha provisória entra no fim e a conversa desce até ela.
  const pendingCountRef = useRef(0);
  useEffect(() => {
    if (pendingMessages.length > pendingCountRef.current) {
      clearUnreadMarker();
      scrollToBottom();
    }
    pendingCountRef.current = pendingMessages.length;
  }, [pendingMessages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // o POST já devolveu a mensagem gravada: entra na lista sem esperar o socket
    const offReal = onPendingReal((pendingTicketId, message) => {
      if (String(pendingTicketId) !== String(ticketNumericIdRef.current)) return;
      dispatch({ type: "ADD_MESSAGE", payload: message });
      matchRealMessage(pendingTicketId, message);
    });
    // o servidor aceitou, mas a mensagem não apareceu (socket caído?): recarrega a 1ª página
    const offStale = onPendingStale((pendingTicketId) => {
      if (String(pendingTicketId) !== String(ticketNumericIdRef.current)) return;
      const uuid = currentTicketId.current;
      api
        .get("/messages/" + uuid, {
          params: { pageNumber: 1, selectedQueues: JSON.stringify(selectedQueuesMessage) },
        })
        .then(({ data }) => {
          if (currentTicketId.current !== uuid || !Array.isArray(data?.messages)) return;
          dispatch({ type: "MERGE_MESSAGES", payload: data.messages });
          reconcilePending(pendingTicketId, data.messages);
        })
        .catch(() => {});
    });
    return () => {
      offReal();
      offStale();
    };
  }, [selectedQueuesMessage]);

  // Carregou mensagens antigas no topo: mantém na tela o ponto que o atendente estava lendo.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && prependAnchorRef.current !== null) {
      el.scrollTop = el.scrollHeight - prependAnchorRef.current;
      prependAnchorRef.current = null;
    }
  }, [messagesList]);

  useEffect(() => {
    // página que o "ir até a mensagem" já carregou por conta própria
    if (skipFetchPageRef.current === pageNumber) {
      skipFetchPageRef.current = null;
      return undefined;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            if (pageNumber > 1 && listRef.current) {
              prependAnchorRef.current = listRef.current.scrollHeight - listRef.current.scrollTop;
            }
            if (pageNumber === 1) {
              unreadFromListRef.current = Number(data.ticket?.unreadMessages) || 0;
            }
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);
            if (pageNumber === 1) {
              setFirstPageTicket(ticketId);
              reconcilePending(data.ticket?.id, data.messages);
            }
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom({ keepUnreadDivider: true });
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
      // Mensagem que chegou enquanto o socket estava caído só aparecia depois de recarregar a
      // página: ao reconectar, busca a primeira página de novo e mescla.
      api
        .get("/messages/" + ticketId, {
          params: { pageNumber: 1, selectedQueues: JSON.stringify(selectedQueuesMessage) },
        })
        .then(({ data }) => {
          if (currentTicketId.current === ticketId && Array.isArray(data?.messages)) {
            dispatch({ type: "MERGE_MESSAGES", payload: data.messages });
            reconcilePending(data.ticket?.id, data.messages);
          }
        })
        .catch(() => {});
    }

    // Mensagem que chega com a conversa aberta e a aba visível já foi vista: avisa o backend
    // para zerar o selo de não lidas (antes só zerava ao responder ou reabrir o ticket).
    // Com a aba em segundo plano fica pendente e é marcada quando o atendente voltar.
    let readTimer = null;
    let pendingRead = false;
    const markTicketAsRead = () => {
      if (document.visibilityState !== "visible") {
        pendingRead = true;
        return;
      }
      pendingRead = false;
      clearTimeout(readTimer);
      readTimer = setTimeout(() => {
        api.post(`/messages/${ticketId}/read`).catch(() => {});
      }, 800);
    };
    const onVisibilityChange = () => {
      if (pendingRead && document.visibilityState === "visible") markTicketAsRead();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // o servidor avisa no evento quando não conseguiu entregar ao WhatsApp
    const withSendError = (data) =>
      data.sendError || data.message?.sendError ? { ...data.message, sendError: true } : data.message;

    const onAppMessageMessagesList = (data) => {
      if (data.action === "create" && data.ticket.uuid === ticketId) {
        dispatch({ type: "ADD_MESSAGE", payload: withSendError(data) });
        // a mensagem de verdade chegou: sai a bolha "enviando" correspondente
        if (data.message?.fromMe && !isReactionRow(data.message)) {
          matchRealMessage(data.ticket.id, data.message);
        }
        if (isReactionRow(data.message)) {
          // reação não é mensagem nova: só atualiza os selinhos (e conta como vista)
          if (!data.message.fromMe) markTicketAsRead();
          return;
        }
        // respondeu (daqui ou do celular): a faixa de não lidas perde o sentido
        if (data.message?.fromMe) clearUnreadMarker();
        // Só desce sozinho se o atendente já estava no fim (ou se a mensagem é dele);
        // lendo o histórico, a mensagem nova vira contador no botão de descer.
        if (data.message?.fromMe || nearBottomRef.current) {
          scrollToBottom();
        } else {
          setNewBelow((n) => n + 1);
        }
        if (!data.message?.fromMe) markTicketAsRead();
      }

      // sem o ticket no evento, o redutor só atualiza se a mensagem estiver nesta conversa
      if (data.action === "update" && data?.message && (!data.message.ticket || data.message.ticket.uuid === ticketId)) {
        dispatch({ type: "UPDATE_MESSAGE", payload: withSendError(data) });
      }

      if (data.action == "delete" && data.message.ticket?.uuid === ticketId) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    }
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketId}`)
      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeout(readTimer);
    };

  }, [ticketId]);

  useEffect(() => {
    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    };
  }, [dragTimeout]);

  const cancelJump = () => {
    if (jumpRef.current) jumpRef.current.cancelled = true;
    jumpRef.current = null;
    jumpScrollRef.current = null;
    setJumpState(null);
  };

  // Limite de páginas por busca (20 mensagens cada): não varre um histórico de anos de uma vez.
  const JUMP_MAX_PAGES = 40;

  const jumpToMessage = async ({ id, createdAt }) => {
    if (id === undefined || id === null) return;
    if (scrollToMessage(id)) return;
    if (jumpRef.current) return;
    if (!hasMoreRef.current) {
      toast.info(i18n.t("conversation.jump.notFound"), { toastId: "jump-not-found" });
      return;
    }

    const token = { cancelled: false };
    const uuid = currentTicketId.current;
    const targetTime = createdAt ? new Date(createdAt).getTime() : NaN;
    jumpRef.current = token;
    setJumpState({ loaded: 0 });

    let page = pageNumberRef.current;
    let found = false;
    let loaded = 0;
    try {
      for (let step = 0; step < JUMP_MAX_PAGES; step += 1) {
        page += 1;
        const { data } = await api.get("/messages/" + uuid, {
          params: { pageNumber: page, selectedQueues: JSON.stringify(selectedQueuesMessage) },
        });
        if (token.cancelled || currentTicketId.current !== uuid) return;
        const batch = Array.isArray(data?.messages) ? data.messages : [];
        if (listRef.current) {
          prependAnchorRef.current = listRef.current.scrollHeight - listRef.current.scrollTop;
        }
        skipFetchPageRef.current = page;
        dispatch({ type: "LOAD_MESSAGES", payload: batch });
        setHasMore(!!data.hasMore);
        setPageNumber(page);
        loaded += batch.length;
        found = batch.some((m) => String(m.id) === String(id));
        if (found || !data.hasMore || batch.length === 0) break;
        // já passou da data da mensagem e ela não veio: não está nesta conversa
        if (!Number.isNaN(targetTime) && new Date(batch[0].createdAt).getTime() < targetTime) break;
        setJumpState({ loaded });
      }
    } catch (err) {
      if (!token.cancelled) toastError(err);
    }
    if (token.cancelled) return;
    jumpRef.current = null;
    setJumpState(null);
    if (found) {
      // rola no próximo efeito, com a mensagem já desenhada
      jumpScrollRef.current = id;
      requestAnimationFrame(() => {
        if (jumpScrollRef.current === id && scrollToMessage(id, { smooth: false })) jumpScrollRef.current = null;
      });
    } else {
      toast.info(i18n.t("conversation.jump.notFound"), { toastId: "jump-not-found" });
    }
  };
  const jumpToMessageRef = useRef(jumpToMessage);
  jumpToMessageRef.current = jumpToMessage;

  useEffect(() => onJumpToMessage((detail) => jumpToMessageRef.current(detail)), []);

  // garantia: se o quadro de animação rodou antes de a bolha existir, rola agora
  useEffect(() => {
    const id = jumpScrollRef.current;
    if (id !== null && scrollToMessage(id, { smooth: false })) jumpScrollRef.current = null;
  }, [messagesList]);

  const loadMore = () => {
    if (loadingMore || jumpRef.current) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = ({ keepUnreadDivider = false } = {}) => {
    setTimeout(() => {
      // na abertura, quem manda é a faixa de não lidas (se houver)
      if (keepUnreadDivider && unreadMarkerRef.current && scrollToUnreadDivider()) return;
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({});
      }
    }, 100);
  };

  const handleScrollDownClick = () => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewBelow(0);
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const { scrollTop } = el;
    const nearBottom = el.scrollHeight - scrollTop - el.clientHeight < 140;
    nearBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
    if (nearBottom) setNewBelow(0);

    // A faixa some quando o atendente rola até o fim depois de ter lido o que estava acima.
    if (unreadMarkerRef.current) {
      if (!nearBottom) unreadLeftBottomRef.current = true;
      else if (unreadLeftBottomRef.current) clearUnreadMarker();
    }

    if (!hasMore) return;

    if (scrollTop === 0) {
      el.scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null);
    setReplyingMessage(message);
  };

  // Dá para reagir a mensagem de verdade do WhatsApp (nota interna, apagada e chamada não).
  const canReact = (message) =>
    !!message &&
    typeof message.id === "number" &&
    !message.isDeleted &&
    !message.isPrivate &&
    message.mediaType !== "call_log" &&
    // o servidor recusa reação em ticket encerrado
    ticketStatus !== "closed" &&
    (channel === undefined || channel === "whatsapp" || channel === "whatsapp_oficial");

  const openReactionPicker = (anchor, message, startOnPicker = false) => {
    setAnchorEl(null);
    setReactionTarget({ anchorEl: anchor, message, startOnPicker });
  };

  const handlePickReaction = (emoji) => {
    const target = reactionTarget;
    setReactionTarget(null);
    if (target) reactTo(target.message, emoji);
  };

  // Toque longo na bolha (celular/tablet) abre a barra de reações.
  const cancelLongPress = () => {
    clearTimeout(longPressRef.current);
    longPressRef.current = null;
  };
  const bubbleTouchProps = (message) => {
    if (!canReact(message) || showSelectMessageCheckbox) return {};
    return {
      onTouchStart: (e) => {
        const el = e.currentTarget;
        cancelLongPress();
        longPressRef.current = setTimeout(() => openReactionPicker(el, message), 550);
      },
      onTouchEnd: cancelLongPress,
      onTouchMove: cancelLongPress,
      onTouchCancel: cancelLongPress,
    };
  };

  const renderReactButton = (message) =>
    canReact(message) && !showSelectMessageCheckbox ? (
      <span className={clsx(classes.reactSlot, message.fromMe ? classes.reactSlotBefore : classes.reactSlotAfter)}>
        <IconButton
          className={classes.reactBtn}
          aria-label={i18n.t("conversation.reactions.react")}
          title={i18n.t("conversation.reactions.react")}
          onClick={(e) => openReactionPicker(e.currentTarget, message)}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <InsertEmoticon />
        </IconButton>
      </span>
    ) : null;

  // Mensagem gravada que nunca saiu: o servidor a remove (DELETE não mexe no WhatsApp quando a
  // mensagem é nossa e está sem confirmação) e avisa a tela pelo socket.
  const deleteUnsent = async (message) => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      // já não existe mais: tudo certo
      if (err?.response?.status !== 404) throw err;
    }
    dispatch({ type: "DELETE_MESSAGE", payload: message.id });
  };

  const handleDiscardUnsent = async (message) => {
    setResendState((prev) => ({ ...prev, [message.id]: "busy" }));
    try {
      await deleteUnsent(message);
    } catch (err) {
      toastError(err);
    }
    setResendState((prev) => ({ ...prev, [message.id]: undefined }));
  };

  // "Tentar de novo": tira a que não saiu e manda o mesmo conteúdo como mensagem nova.
  const handleResend = async (message) => {
    const targetTicket = ticketNumericIdRef.current || message.ticketId;
    setResendState((prev) => ({ ...prev, [message.id]: "busy" }));
    try {
      // o anexo é buscado antes de apagar a mensagem (apagar remove o arquivo do servidor)
      const send = await resendMessage(message, targetTicket);
      await deleteUnsent(message);
      send();
    } catch (err) {
      toastError(err);
    }
    setResendState((prev) => ({ ...prev, [message.id]: undefined }));
  };

  const renderSendError = (message) => {
    if (!isUnsent(message)) return null;
    const busy = resendState[message.id] === "busy";
    return (
      <SendErrorRow
        onRetry={() => handleResend(message)}
        onDiscard={() => handleDiscardUnsent(message)}
        busy={busy}
      />
    );
  };

  const renderReactions = (message) => {
    const reactions = reactionsById.get(message.id);
    if (!reactions) return null;
    return <ReactionChips reactions={reactions} fromMe={message.fromMe} onRemove={() => reactTo(message, "")} />;
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    const last = segments[segments.length - 1];
    // a URL vem codificada (acento vira %C3…); o corpo guarda o nome como ele é
    try {
      return decodeURIComponent(last);
    } catch (e) {
      return last;
    }
  };

  const checkMessageMedia = (message) => {
    // Áudio é o que o servidor marcou como áudio. Só para mensagens antigas, gravadas sem
    // tipo, vale a extensão do arquivo (antes qualquer URL com ".webm" ou texto com "audio_"
    // virava player de áudio).
    const isAudioMessage = (message) => {
      if (["audio", "audioMessage", "ptt"].includes(message.mediaType)) return true;
      if (!message.mediaType && message.mediaUrl) {
        const path = String(message.mediaUrl).toLowerCase().split("?")[0].split("#")[0];
        return /\.(mp3|wav|ogg|oga|opus|m4a|aac)$/.test(path);
      }
      return false;
    };

    // Templates
    if (message.mediaType === "template") {
      return <Template message={message} />;
    }

    // Localização
    else if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = locationParts.length > 2 ? locationParts[2] : null;

      return <LocationPreview 
        image={imageLocation} 
        link={linkLocation} 
        description={descriptionLocation} 
      />;
    }

    // Contatos
    else if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      
      return <VcardPreview 
        contact={contact} 
        numbers={obj[0]?.number} 
        queueId={message?.ticket?.queueId} 
        whatsappId={message?.ticket?.whatsappId} 
        channel={channel} 
      />;
    }

    else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
      // ✅ CORREÇÃO: Parse correto dos dados - formato: image|sourceUrl|title|body|messageUser
      let [image, sourceUrl, title, body, messageUser] = message.body.split('|');
      
      // Fallback para messageUser se não estiver presente
      if (!messageUser || messageUser.trim() === "") {
        messageUser = "Olá! Tenho interesse e queria mais informações, por favor.";
      }
      
      return <AdMetaPreview 
        image={image} 
        sourceUrl={sourceUrl} 
        title={title} 
        body={body} 
        messageUser={messageUser} 
      />;
    }

    // PDF: cartão de arquivo; o clique abre o visualizador na tela
    else if (isPdfUrl(message.mediaUrl, message.body, message.mediaType)) {
      return (
        <FileCard
          url={message.mediaUrl}
          fallbackName={message.body || "documento.pdf"}
          onDownload={(url, name) => downloadPdf(url, name)}
        />
      );
    }

    // Áudio
    else if (isAudioMessage(message)) {
      return (
        <div style={{
          width: '100%',
          maxWidth: '300px',
          padding: '8px',
          backgroundColor: 'transparent'
        }}>
          <AudioModal
            key={message.mediaUrl}
            url={message.mediaUrl}
            message={message}
          />
        </div>
      );
    }

    // Imagens
    else if (message.mediaType === "image") {
      return (
        <ModalImageCors
          imageUrl={message.mediaUrl}
          placeholderUrl={getPreviewFor(message.id)}
          onOpen={() => setGalleryStartId(message.id)}
        />
      );
    }

    // Vídeos (estado de carregando/erro por vídeo)
    else if (message.mediaType === "video") {
      return (
        <VideoBubble
          key={message.mediaUrl}
          url={message.mediaUrl}
          fileName={getBasename(message.mediaUrl)}
          className={clsx(classes.messageMedia, classes.mediaSurface)}
          errorClassName={classes.mediaError}
          onOpen={() => setGalleryStartId(message.id)}
        />
      );
    }

    // Qualquer outro arquivo: cartão com nome, tipo e botão de baixar
    else if (message.mediaUrl) {
      return (
        <FileCard
          url={message.mediaUrl}
          fallbackName={message.body || "arquivo"}
          onDownload={(url, name) => downloadPdf(url, name)}
        />
      );
    }

    return null;
  };

  const renderMessageAck = (message) => {
    if (isUnsent(message)) {
      return <ErrorOutline fontSize="small" className={classes.ackError} />;
    }
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    } else
      // escala do Baileys: 1 pendente · 2 chegou ao servidor (1 tique) · 3 entregue (2 tiques) · 4 lida
      if (message.ack === 1 || message.ack === 2) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      } else
        if (message.ack === 3) {
          return <DoneAll fontSize="small" className={classes.ackIcons} />;
        } else
          if (message.ack === 4) {
            return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
          } else
            if (message.ack === 5) {
              return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
            }
  };

  // "Hoje", "Ontem", dia da semana (última semana) ou a data.
  const dayLabel = (iso) => {
    const day = parseISO(iso);
    const now = new Date();
    if (isSameDay(day, now)) return "Hoje";
    if (isSameDay(day, subDays(now, 1))) return "Ontem";
    if (differenceInCalendarDays(now, day) < 7) return format(day, "EEEE", { locale: ptBR });
    return format(day, "dd/MM/yyyy");
  };

  const renderDailyTimestamps = (message, index) => {
    const isNewDay =
      index === 0 ||
      !isSameDay(parseISO(message.createdAt), parseISO(viewList[index - 1].createdAt));
    if (!isNewDay) return null;
    return (
      <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
        <div className={classes.dailyTimestampText}>{dayLabel(message.createdAt)}</div>
      </span>
    );
  };

  // Primeira bolha de uma sequência do mesmo remetente (ganha o rabicho e mais respiro).
  const isFirstOfGroup = (message, index) => {
    if (index === 0) return true;
    const prev = viewList[index - 1];
    return (
      prev.fromMe !== message.fromMe ||
      prev.mediaType === "call_log" ||
      !!prev.isPrivate !== !!message.isPrivate ||
      prev.ticketId !== message.ticketId ||
      (isGroup && !message.fromMe && prev.contact?.id !== message.contact?.id) ||
      !isSameDay(parseISO(prev.createdAt), parseISO(message.createdAt))
    );
  };

  const renderTicketsSeparator = (message, index) => {
    let lastTicket = viewList[index - 1]?.ticketId;
    let currentTicket = message.ticketId;

    if (lastTicket !== currentTicket && lastTicket !== undefined) {
      if (message?.ticket?.queue) {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}a`}
          >
            <div className={classes.currentTicktText}>
              <span
                className={classes.currentTickDot}
                style={{ backgroundColor: message?.ticket?.queue?.color || "currentColor" }}
              />
              #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
            </div>

          </span>
        );
      } else {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}b`}
          >
            <div className={classes.currentTicktText}>
              <span className={classes.currentTickDot} style={{ backgroundColor: "currentColor" }} />
              #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
            </div>

          </span>
        );
      }
    }

  };

  // O respiro entre remetentes vem da própria bolha (tailLeft/tailRight); aqui só entra a
  // faixa de não lidas, antes da primeira mensagem que o atendente ainda não viu.
  const renderMessageDivider = (message) =>
    unreadMarker && unreadMarker.beforeId === message.id ? (
      <UnreadDivider ref={unreadDividerRef} count={unreadMarker.count} />
    ) : null;

  // Clique na citação leva até a mensagem original; se ela ainda não está na tela, as
  // páginas antigas são carregadas até encontrá-la.
  const goToQuotedMessage = (e, quoted) => {
    e.stopPropagation();
    if (!quoted?.id) return;
    requestJumpToMessage({ id: quoted.id, createdAt: quoted.createdAt });
  };

  const renderQuotedMessage = (message) => {

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
        style={{ cursor: "pointer" }}
        onClick={(e) => goToQuotedMessage(e, message.quotedMsg)}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <AudioModal url={message.quotedMsg.mediaUrl} />
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <div style={{ maxWidth: "300px", width: "100%" }}>
                <video
                  className={clsx(classes.messageMedia, classes.mediaSurface)}
                  src={message.quotedMsg.mediaUrl}
                  controls
                  preload="metadata"
                  style={{ 
                    width: "100%", 
                    height: "auto", 
                    maxHeight: "200px",
                  }}
                  onError={(e) => {
                    console.error("❌ Erro ao carregar vídeo citado:", e);
                  }}
                >
                  <source src={message.quotedMsg.mediaUrl} type="video/mp4" />
                  <source src={message.quotedMsg.mediaUrl} type="video/webm" />
                  <source src={message.quotedMsg.mediaUrl} type="video/ogg" />
                  <div className={classes.mediaErrorText}>
                    ❌ Erro ao carregar vídeo
                  </div>
                </video>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "contactMessage"
            && (
              "Contato"
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  variant="outlined"
                  target="_blank"
                  href={message.quotedMsg.mediaUrl}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image"
            && (
              <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
            || renderBodyText(message.quotedMsg?.body, message.quotedMsg, true)}

          {!message.quotedMsg.mediaType === "image" && message.quotedMsg?.body}

        </div>
      </div>
    );
  };

  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      const hasFiles = event.dataTransfer &&
        event.dataTransfer.types &&
        (event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('application/x-moz-file'));

      if (hasFiles) {
        if (dragTimeout) {
          clearTimeout(dragTimeout);
        }

        const timeout = setTimeout(() => {
          if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
            setDragActive(true);
          }
        }, 100);

        setDragTimeout(timeout);
      }
    } else if (event.type === "dragleave") {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  }

  const isYouTubeLink = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  // Cartão de prévia de link, quando a mensagem do WhatsApp trouxe os dados (vídeo do YouTube
  // já tem a prévia própria).
  const renderLinkPreview = (message) => {
    if (message.mediaUrl || message.isDeleted || isYouTubeLink(message.body)) return null;
    const preview = linkPreviewOf(message);
    return preview ? <LinkPreviewCard preview={preview} /> : null;
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();

    if (dragTimeout) {
      clearTimeout(dragTimeout);
      setDragTimeout(null);
    }

    setDragActive(false);

    if (event.dataTransfer.files &&
      event.dataTransfer.files.length > 0 &&
      event.dataTransfer.files[0] instanceof File) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }
  // Sem a flag "g": com ela o .test() guarda a posição (lastIndex) entre chamadas, e como o teste
  // roda duas vezes por mensagem (ramo xml e ramo texto), o resultado dependia da mensagem
  // anterior — texto com "<...>" (saída de traceroute, por exemplo) saía com a bolha em branco.
  const xmlRegex = /<([^>]+)>/;
  const boldRegex = /\*(.*?)\*/g;

  const formatXml = (xmlString) => {
    // Verifica se o XML contém a assinatura com nome do atendente
    // Formata o texto dentro da assinatura em negrito
    return xmlString.replace(boldRegex, "**$1**");
  };

  const renderMessages = () => {

    if (viewList.length > 0) {
      const viewMessagesList = viewList.map((message, index) => {
        if (message.pending) {
          const entry = message.pending;
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              <PendingBubble
                entry={{ ...entry, onDiscard: () => removePending(entry.localId) }}
                className={clsx(
                  entry.isPrivate ? classes.messageRightPrivate : classes.messageRight,
                  isFirstOfGroup(message, index) && classes.tailRight
                )}
                bubbleClasses={classes}
                renderText={(text) => renderBodyText(text, message)}
                quoted={entry.quotedMsg ? renderQuotedMessage(message) : null}
              />
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill={SEMANTIC.error} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                id={`msg-${message.id}`}
                className={clsx(
                  classes.messageLeft,
                  isFirstOfGroup(message, index) && classes.tailLeft,
                  showSelectMessageCheckbox && classes.selectingLeft,
                  reactionsById.has(message.id) && classes.withReactions
                )}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                {...bubbleTouchProps(message)}
              >
                {renderReactButton(message)}
                {renderReactions(message)}
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply className={classes.forwardIcon} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && isFirstOfGroup(message, index) && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}

                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    >🚫 Essa mensagem foi apagada pelo contato &nbsp;
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template" || message.mediaType === "adMetaPreview"
                ) && checkMessageMedia(message)}

                <div className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderLinkPreview(message)}
                  {
                    message.mediaType !== "adMetaPreview" && !bodyIsOnlyFileName(message) && (
                      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && getBasename(message.mediaUrl).trim() !== message.body.trim()) ||
                      message.mediaType !== "audio" &&
                      message.mediaType !== "image" &&
                      message.mediaType !== "video" &&
                      message.mediaType != "reactionMessage" &&
                      message.mediaType != "locationMessage" &&
                      message.mediaType !== "contactMessage" &&
                      message.mediaType !== "template"
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <span>{message.body}</span>

                        )}
                        {!xmlRegex.test(message.body) &&
                          renderBodyText(
                            (lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " : message.body,
                            message
                          )}

                      </>

                    )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                id={`msg-${message.id}`}
                className={clsx(
                  message.isPrivate ? classes.messageRightPrivate : classes.messageRight,
                  isFirstOfGroup(message, index) && classes.tailRight,
                  reactionsById.has(message.id) && classes.withReactions
                )}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                {...bubbleTouchProps(message)}
              >
                {renderReactButton(message)}
                {renderReactions(message)}
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply className={classes.forwardIcon} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}
                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    >🚫 Essa mensagem foi apagada &nbsp;
                    </span>
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "template"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                  // com o rodapé de erro, a hora ancora no bloco de texto (senão cairia sobre os botões)
                  style={isUnsent(message) ? { position: "relative" } : undefined}
                >

                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderLinkPreview(message)}

                  {
                    ((message.mediaType === "image" || message.mediaType === "video") && getBasename(message.mediaUrl) === message.body) ||
                    (message.mediaType !== "audio" && message.mediaType != "reactionMessage" && message.mediaType != "locationMessage" && message.mediaType !== "contactMessage" && message.mediaType !== "template" && !bodyIsOnlyFileName(message)) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <div>{formatXml(message.body)}</div>

                        )}
                        {!xmlRegex.test(message.body) && renderBodyText(message.body, message)}

                      </>
                    )}

                  <span className={classes.timestamp}>
                    {message.isPrivate && <LockIcon className={classes.privateIcon} />}
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
                {renderSendError(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      // Uma seção por dia: a data flutuante gruda só enquanto as mensagens daquele dia rolam
      // e é empurrada para fora pela data seguinte (sem empilhar duas datas no topo).
      const sections = [];
      viewList.forEach((message, index) => {
        const isNewDay =
          index === 0 ||
          !isSameDay(parseISO(message.createdAt), parseISO(viewList[index - 1].createdAt));
        // chave = o dia (não a 1ª mensagem): ao carregar mensagens antigas do mesmo dia a seção
        // não é remontada, então imagens já carregadas não piscam nem mudam a altura da lista
        if (isNewDay) {
          const day = format(parseISO(message.createdAt), "yyyy-MM-dd");
          const repeated = sections.filter((sec) => sec.day === day).length;
          sections.push({ day, key: repeated ? `${day}-${repeated}` : day, items: [] });
        }
        sections[sections.length - 1].items.push(viewMessagesList[index]);
      });
      return sections.map((section) => (
        <div key={`day-${section.key}`} className={classes.daySection}>
          {section.items}
        </div>
      ));
    } else {
      return <div className={classes.emptyMessages}>Diga olá para seu novo contato!</div>;
    }
  };
const shouldBlurMessages = ticketStatus === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
        channel={channel}
        ticketStatus={ticketStatus}
        canReact={canReact(selectedMessage)}
        myReaction={reactionsById.get(selectedMessage?.id)?.mine || null}
        onReact={(emoji) => reactTo(selectedMessage, emoji)}
        onReactMore={() =>
          // o botão do menu some quando o mouse sai da bolha: a âncora é a própria bolha
          openReactionPicker(document.getElementById(`msg-${selectedMessage.id}`) || anchorEl, selectedMessage, true)
        }
      />
      {galleryStartId !== null && galleryItems.length > 0 && (
        <MediaGallery items={galleryItems} startId={galleryStartId} onClose={() => setGalleryStartId(null)} />
      )}
      <ReactionPicker
        open={Boolean(reactionTarget)}
        anchorEl={reactionTarget?.anchorEl || null}
        mine={reactionTarget ? reactionsById.get(reactionTarget.message.id)?.mine || null : null}
        startOnPicker={!!reactionTarget?.startOnPicker}
        onPick={handlePickReaction}
        onClose={() => setReactionTarget(null)}
      />
      

      <div className={classes.listArea}>
        <div
          id="messagesList"
          ref={listRef}
          className={classes.messagesList}
          onScroll={handleScroll}
          style={{
            filter: shouldBlurMessages ? "blur(4px)" : "none",
            pointerEvents: shouldBlurMessages ? "none" : "auto"
          }}
        >
          {viewList.length > 0 ? renderMessages() : []}
          <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />
        </div>
        {jumpState && (
          <div className={classes.jumpNotice} role="status" aria-live="polite">
            <CircularProgress size={16} thickness={5} />
            <span className={classes.jumpNoticeText}>
              {i18n.t("conversation.jump.searching")}
              {jumpState.loaded > 0 && (
                <span className={classes.jumpNoticeCount}>
                  {" · "}
                  {i18n.t("conversation.jump.loaded", { count: jumpState.loaded })}
                </span>
              )}
            </span>
            <IconButton
              className={classes.jumpNoticeClose}
              onClick={cancelJump}
              aria-label={i18n.t("conversation.jump.cancel")}
              title={i18n.t("conversation.jump.cancel")}
            >
              <Close />
            </IconButton>
          </div>
        )}
        {showScrollDown && (
          <IconButton
            className={classes.scrollDownBtn}
            onClick={handleScrollDownClick}
            aria-label="Ir para o fim da conversa"
          >
            {newBelow > 0 && <span className={classes.scrollDownBadge}>{newBelow}</span>}
            <KeyboardArrowDown />
          </IconButton>
        )}
      </div>

      {(channel !== "whatsapp" && channel !== "webchat" && channel !== undefined) && (
        <div className={classes.channelNotice}>
          {channel === "facebook" ? (
            <Facebook />
          ) : channel === "instagram" ? (
            <Instagram />
          ) : (
            <WhatsApp />
          )}

          <span>
            Você tem 24h para responder após receber uma mensagem, de acordo
            com as políticas da Meta.
          </span>
        </div>
      )}
      
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
